import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';

import { Subscription } from 'rxjs';

import { ModuleItem } from '../../../../../models/module-item';
import { Study } from '../../../../../models/study';
import { ModuleItemAvailableSubType, ModuleItemSubType } from '../../../../../models/module-item-sub-type';
import { Language } from '../../../../../models/language';
import { selectSelectedLanguage, selectSupportedLanguages } from '../../../../state/study/study.selectors';
import { AppState } from '../../../../../state';
import { ModuleItemAvailableAttribute } from '../../../../../models/module-item-attribute';
import { MODULE_ITEM_QUESTION_IDENTIFIER } from '../../../../../core/dashboard-constants';
import { ModuleItemAvailableType } from '../../../../../models/module-item-type';
import {
  ModuleItemRoutingGroup,
  OtherwiseRouteToSpecial,
  OtherwiseRouteToSpecialIndex,
  OtherwiseRouteToSpecialQuestion,
} from '../../../../../models/module-item-routing-group';
import { ModuleItemRoutingGroupExpression } from '../../../../../models/module-item-routing-group-expression';
import { getNumberChoices, getStringChoices } from '../../../helpers/module-item-helper';
import { ModuleItemRouting } from '../../../../../models/module-item-routing';
import { StudyType } from '../../../../../models/study-type';
import { RoutingService } from '../../../../services/routing/routing.service';
import { ExpressionOperators } from '../../../../../models/expression-operators';
import { selectModuleItems } from '../../../../state/module-item/module-item.selectors';

@Component({
  selector: 'dashboard-module-item-routing',
  templateUrl: './module-item-routing.component.html',
  styleUrls: ['./module-item-routing.component.scss'],
})
export class ModuleItemRoutingComponent implements OnInit, OnDestroy {
  @Input() dialogData;

  selectedStudy: Study;
  moduleItemSubType: ModuleItemSubType;
  selectedModuleItem: ModuleItem;
  selectedModuleItems: ModuleItem[];

  supportedLanguages: Language[];
  selectedLanguage: Language;

  jumpToList = [];
  canRouteList = [];
  expressionOptions = [];
  routing: ModuleItemRouting;

  ModuleItemAvailableType = ModuleItemAvailableType;

  private subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef,
    private routingService: RoutingService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((selectedLanguage) => {
        this.selectedLanguage = selectedLanguage;
        this.changeDetectorRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.store
        .select(selectModuleItems)
        .subscribe((selectedModuleItems) => (this.selectedModuleItems = selectedModuleItems))
    );
    this.selectedStudy = this.dialogData?.selectedStudy;
    this.moduleItemSubType = this.dialogData?.moduleItemSubType;
    this.selectedModuleItem = this.dialogData?.moduleItem;

    this.fillItemLists();

    if (this.selectedModuleItem?.routing) {
      this.routing = JSON.parse(JSON.stringify(this.selectedModuleItem.routing));
      if (this.routing.otherwiseRouteToSpecial === OtherwiseRouteToSpecial.NEXT_MODULE) {
        this.routing.otherwiseRouteToId = OtherwiseRouteToSpecialIndex.NEXT_MODULE;
      } else if (this.routing.otherwiseRouteToSpecial === OtherwiseRouteToSpecial.END_OF_SURVEY) {
        this.routing.otherwiseRouteToId = OtherwiseRouteToSpecialIndex.END_OF_SURVEY;
      }

      this.routing.routingGroups.forEach((group) => {
        if (group.routeToSpecial === OtherwiseRouteToSpecial.NEXT_MODULE) {
          group.routeToId = OtherwiseRouteToSpecialIndex.NEXT_MODULE;
        } else if (group.routeToSpecial === OtherwiseRouteToSpecial.END_OF_SURVEY) {
          group.routeToId = OtherwiseRouteToSpecialIndex.END_OF_SURVEY;
        }
        group.expressions.forEach((expression) => {
          this.initExpressionOptions(expression);
        });
      });
    } else {
      this.routing = {};
      this.routing.routingGroups = [];
      if (this.jumpToList.length) {
        this.routing.otherwiseRouteToId = this.jumpToList[0].id;
      }
    }

    if (!this.canRouteList.length || this.jumpToList.length <= 1) {
      this.routing = null;
    }
  }

  fillItemLists(): void {
    const selectedModuleItemIndex =
      this.selectedModuleItems?.findIndex((moduleItem) => moduleItem.id === this.selectedModuleItem?.id) || 0;

    this.selectedModuleItems?.forEach((moduleItem, index) => {
      const question = moduleItem.translations?.find(
        (translation) =>
          translation?.language === this.selectedLanguage.code &&
          translation?.identifier === MODULE_ITEM_QUESTION_IDENTIFIER
      );
      const listItem = {
        id: moduleItem.id,
        question: question?.translation,
        sortOrder: index,
        canRoute:
          moduleItem.type === ModuleItemAvailableType.TEXT ||
          moduleItem.type === ModuleItemAvailableType.NUMBER ||
          (moduleItem.type === ModuleItemAvailableType.SPECIAL &&
            moduleItem.subType === ModuleItemAvailableSubType.IVY),
      };

      if (index > selectedModuleItemIndex) {
        this.jumpToList.push(listItem);
      }

      if (listItem.canRoute && index <= selectedModuleItemIndex) {
        this.canRouteList.push(listItem);
      }
    });

    if (this.selectedStudy?.type === StudyType.CC) {
      this.jumpToList.push({
        id: OtherwiseRouteToSpecialIndex.NEXT_MODULE,
        question: OtherwiseRouteToSpecialQuestion.NEXT_MODULE,
        canRoute: false,
      });
      this.jumpToList.push({
        id: OtherwiseRouteToSpecialIndex.END_OF_SURVEY,
        question: OtherwiseRouteToSpecialQuestion.END_OF_SURVEY,
        canRoute: false,
      });
    }
  }

  initExpressionOptions(expression: ModuleItemRoutingGroupExpression): void {
    const moduleItem: ModuleItem = this.getModuleItemById(expression.moduleItemId);

    const answerOptions = this.getAnswerOptions(moduleItem);

    this.expressionOptions[expression.moduleItemId] = {
      operators: this.getOperators(moduleItem),
      answerOptions,
      itemType: moduleItem.type,
      itemSubType: moduleItem.subType,
    };

    if (!expression.answer && answerOptions) {
      expression.answer = answerOptions[0];
    }
  }

  getModuleItemById(id: number): ModuleItem {
    return this.selectedModuleItems?.find((moduleItem: ModuleItem) => moduleItem.id === id);
  }

  getAnswerOptions(moduleItem: ModuleItem): number[] | string[] {
    if (moduleItem.subType === ModuleItemAvailableSubType.YES_NO) {
      return ['no', 'yes'];
    } else if (
      moduleItem.type === ModuleItemAvailableType.NUMBER &&
      (moduleItem.subType === ModuleItemAvailableSubType.DROPDOWN ||
        moduleItem.subType === ModuleItemAvailableSubType.SLIDER ||
        moduleItem.subType === ModuleItemAvailableSubType.SINGLE_CHOICE)
    ) {
      const minValueAttribute = parseInt(
        moduleItem.attributes.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.MIN_VALUE).value,
        10
      );
      const maxValueAttribute = parseInt(
        moduleItem.attributes.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.MAX_VALUE).value,
        10
      );
      const stepSizeValueAttribute = parseInt(
        moduleItem.attributes.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.STEP_SIZE).value,
        10
      );

      return getNumberChoices(minValueAttribute, maxValueAttribute, stepSizeValueAttribute);
    } else if (
      moduleItem.subType === ModuleItemAvailableSubType.DROPDOWN ||
      moduleItem.subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE ||
      moduleItem.subType === ModuleItemAvailableSubType.LIKERT
    ) {
      return getStringChoices(moduleItem, this.selectedLanguage);
    }

    return [];
  }

  getOperators(moduleItem: ModuleItem): any[] {
    let operators = [ExpressionOperators.EQUAL_TO, ExpressionOperators.NOT_EQUAL_TO];

    if (moduleItem.type === ModuleItemAvailableType.NUMBER || moduleItem.subType === ModuleItemAvailableSubType.IVY) {
      operators = [
        ...operators,
        ...[
          ExpressionOperators.GREATER_THAN,
          ExpressionOperators.LESS_THAN,
          ExpressionOperators.GREATER_THAN_OR_EQUAL_TO,
          ExpressionOperators.LESS_THAN_OR_EQUAL_TO,
        ],
      ];
    }

    return operators;
  }

  addRoutingGroup(index: number = 0): void {
    const routingGroup: ModuleItemRoutingGroup = {};

    routingGroup.expressions = [];
    routingGroup.routeToId = this.jumpToList[0].id;
    routingGroup.operator = '&&';

    this.addExpression(routingGroup);
    this.routing.routingGroups.splice(index + 1, 0, routingGroup);
  }

  removeRoutingGroup(index: number = 0): void {
    this.routing.routingGroups.splice(index, 1);
    this.routingService.setRoutingData(this.routing);
  }

  addExpression(routingGroup: ModuleItemRoutingGroup, index: number = 0): void {
    const expression: ModuleItemRoutingGroupExpression = {};

    expression.moduleItemId = this.canRouteList[0].id;
    expression.operator = ExpressionOperators.EQUAL_TO.operator;
    this.initExpressionOptions(expression);
    routingGroup.expressions.splice(index + 1, 0, expression);
  }

  removeExpression(routingGroup: ModuleItemRoutingGroup, index: number = 0): void {
    routingGroup.expressions.splice(index, 1);
    this.routingService.setRoutingData(this.routing);
  }

  saveRouting(): void {
    this.routingService.setRoutingData(this.routing);
    this.toastrService.info('Configuration is done, please save module item in order to apply routing changes.');
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
